// AGC: FarmProHub Settings Modal
// AGC: Displays wallet information - matches Origami Battles design 100% (wallet info only, no enable buttons)

import React, { useState, useMemo } from 'react'
import { X, Copy, Check, LogOut } from 'lucide-react'
import { useAccount, useChainId, useDisconnect } from 'wagmi'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'

// AGC: User profile type from Farcaster Mini App SDK
interface FarcasterUser {
  fid: number
  username?: string
  displayName?: string
  pfpUrl?: string
  bio?: string
}

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

// AGC: Hub version constant
const HUB_VERSION = '1.0.0'

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { disconnect } = useDisconnect()
  const { isBaseAppContext } = useFeatureFlags()
  const [copied, setCopied] = useState(false)
  
  // AGC: Use MiniKit hook to get user profile data in Base App (if available)
  // AGC: Try to access MiniKit dynamically to avoid import errors if package not installed
  const miniKit = useMemo(() => {
    if (!isBaseAppContext || typeof window === 'undefined') {
      return null
    }
    
    try {
      // AGC: Try to dynamically import MiniKit if available
      // AGC: This will be null if @coinbase/onchainkit is not installed (desktop mode)
      // AGC: TODO: Add @coinbase/onchainkit to hub package.json if Base App support is needed
      return null // AGC: Placeholder - will be implemented when MiniKit is added
    } catch (error) {
      console.debug('AGC: MiniKit not available (expected in desktop mode)')
      return null
    }
  }, [isBaseAppContext])
  
  const miniKitContext = miniKit?.context

  // AGC: Extract Farcaster user data from MiniKit context
  const farcasterUser: FarcasterUser | null = useMemo(() => {
    if (!isBaseAppContext) {
      return null
    }

    if (!miniKit) {
      return null
    }

    if (!miniKitContext) {
      return null
    }

    if (!miniKitContext.user) {
      return null
    }

    const user = miniKitContext.user
    const userData: FarcasterUser = {
      fid: user.fid || 0,
      username: user.username,
      displayName: user.displayName,
      pfpUrl: user.pfpUrl,
      bio: user.bio
    }

    // AGC: Only return user data if we have at least a FID
    if (userData.fid > 0) {
      return userData
    }

    return null
  }, [isBaseAppContext, miniKit, miniKitContext])

  if (!isOpen) {
    return null
  }

  const formatAddress = (addr: string) => {
    if (!addr) return 'Not Connected'
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`
  }

  const getNetworkName = (id: number) => {
    if (id === 8453) return 'Base Mainnet'
    if (id === 84532) return 'Base Sepolia'
    return `Chain ${id}`
  }

  const handleCopyAddress = async () => {
    if (!address) return
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy address:', err)
    }
  }

  const handleDisconnect = () => {
    disconnect()
    onClose() // AGC: Close modal after disconnecting
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-slate-900 border border-blue-500/40 rounded-2xl shadow-2xl p-6 space-y-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
          aria-label="Close settings"
        >
          <X className="w-4 h-4" />
        </button>

        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Settings</h2>
          <p className="text-sm text-slate-300">FarmProHub wallet information.</p>
        </div>

        <div className="space-y-4">
          {/* AGC: Wallet Information Section - matches Origami Battles design exactly */}
          <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-white">Wallet Information</h3>
            
            {/* AGC: Farcaster Profile (Base App) - Integrated into Wallet Information */}
            {isBaseAppContext && farcasterUser && (
              <div className="flex items-center gap-3 pb-3 border-b border-slate-700">
                {farcasterUser.pfpUrl && (
                  <img
                    src={farcasterUser.pfpUrl}
                    alt="Profile"
                    className="w-10 h-10 rounded-full border-2 border-blue-500/50 object-cover"
                    onError={(e) => {
                      // AGC: Hide image if it fails to load
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {farcasterUser.displayName || farcasterUser.username || 'Unknown User'}
                  </div>
                  {farcasterUser.username && (
                    <div className="text-xs text-slate-400 truncate">
                      @{farcasterUser.username}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Status</span>
                <span className={`text-xs font-semibold ${isConnected ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {isConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              {address && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Address</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-white">{formatAddress(address)}</span>
                      <button
                        onClick={handleCopyAddress}
                        className="p-1 rounded hover:bg-slate-700 transition-colors"
                        title="Copy address"
                      >
                        {copied ? (
                          <Check className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <Copy className="w-3 h-3 text-slate-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Network</span>
                    <span className="text-xs font-semibold text-white">{getNetworkName(chainId)}</span>
                  </div>
                  {isBaseAppContext && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">Environment</span>
                      <span className="text-xs font-semibold text-blue-400">Base App</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* AGC: Version display - matches Origami Battles */}
        <div className="pt-4 border-t border-slate-800">
          <p className="text-xs text-slate-500 text-center">
            Version {HUB_VERSION}
          </p>
        </div>

        <div className="flex justify-between items-center gap-3">
          {isConnected && (
            <button
              type="button"
              onClick={handleDisconnect}
              className="px-4 py-2 rounded-lg bg-red-600/80 text-white font-semibold hover:bg-red-600 transition flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Disconnect
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500 transition ml-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

